/**
 * SupplierDetail.tsx — V5-8-4.
 *
 * Tabs: Profile · Certifications · Data Handling · Risk Assessment · Used By
 */

import { useState, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  PageHeader,
  Select,
  Spinner,
} from '@aigovops/design-system';
import { useOptionalWorkspace } from '../lib/workspace';
import { useRequiredProfile } from '../lib/auth';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import {
  getSupplierCompliance,
  updateSupplierCompliance,
  updateSupplierAssessment,
  updateSupplierProfile,
  checkSupplierTrustPage,
  sendDisclosureRequest,
  getSupplierDisclosure,
  getSupplierDependents,
  type SupplierComplianceDetailRow,
  type SupplierCert,
} from '../lib/api';

type Tab = 'profile' | 'certifications' | 'data' | 'risk' | 'usedby';

const TABS: { value: Tab; label: string }[] = [
  { value: 'profile', label: 'Profile' },
  { value: 'certifications', label: 'Certifications' },
  { value: 'data', label: 'Data Handling' },
  { value: 'risk', label: 'Risk Assessment' },
  { value: 'usedby', label: 'Used By' },
];

const CERT_FRAMEWORKS = [
  { key: 'soc2_type2', label: 'SOC 2 Type II' },
  { key: 'soc2_type1', label: 'SOC 2 Type I' },
  { key: 'iso_27001', label: 'ISO 27001' },
  { key: 'iso_42001', label: 'ISO 42001 (AI)' },
  { key: 'fedramp_li_saas', label: 'FedRAMP Li-SaaS' },
  { key: 'fedramp_moderate', label: 'FedRAMP Moderate' },
  { key: 'fedramp_high', label: 'FedRAMP High' },
  { key: 'hipaa_baa', label: 'HIPAA BAA' },
  { key: 'gdpr_dpa', label: 'GDPR DPA' },
  { key: 'pci_dss', label: 'PCI DSS' },
  { key: 'stateramp', label: 'StateRAMP' },
  { key: 'hitrust', label: 'HITRUST CSF' },
];

const CERT_STATUS_TOKENS: Record<string, { label: string; color: string }> = {
  current: { label: 'Current', color: 'var(--success, var(--accent))' },
  expired: { label: 'Expired', color: 'var(--critical, var(--warning))' },
  pending: { label: 'Pending', color: 'var(--warning)' },
  not_applicable: { label: 'N/A', color: 'var(--text-dim)' },
};

const RISK_TIERS = [
  { value: '', label: 'Not assessed' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

export function SupplierDetail() {
  useRequiredProfile();
  const { vendorId } = useParams<{ vendorId: string }>();
  const [searchParams] = useSearchParams();
  const workspaceCtx = useOptionalWorkspace();
  const slug = workspaceCtx?.workspace.slug ?? '';
  const prefix = workspaceCtx ? `/w/${slug}/app` : '/app';
  const [activeTab, setActiveTab] = useState<Tab>('profile');

  const fromOrg = searchParams.get('from') === 'org';
  const orgSlug = searchParams.get('org');
  const backHref = fromOrg && orgSlug
    ? `/o/${encodeURIComponent(orgSlug)}/suppliers`
    : `${prefix}/suppliers`;

  const q = useWorkspaceQuery(
    (s) => queryKeys.supplierCompliance(s, vendorId ?? ''),
    (s) => vendorId ? getSupplierCompliance(s, vendorId) : Promise.resolve({ detail: null as any }),
  );

  if (q.loading) return <Spinner label="Loading supplier" />;
  if (q.error || !q.data?.detail) {
    return (
      <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
        <Link to={backHref} style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--accent)', textDecoration: 'none' }}>
          &larr; Back to suppliers
        </Link>
        <Alert variant="error">{q.error ? String(q.error) : 'Supplier not found'}</Alert>
      </div>
    );
  }

  const detail = q.data.detail;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <Link to={backHref} style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--accent)', textDecoration: 'none' }}>
        &larr; Back to suppliers
      </Link>
      <PageHeader
        title={detail.supplier_display_name}
        subtitle={detail.vertical ?? undefined}
        right={vendorId ? <Link to={`${prefix}/suppliers/${encodeURIComponent(vendorId)}/register`}>Published AI register</Link> : undefined}
      />

      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        {detail.risk_tier ? <Badge tone={detail.risk_tier === 'critical' || detail.risk_tier === 'high' ? 'accent' : 'neutral'}>{detail.risk_tier} risk</Badge> : <Badge>Unassessed</Badge>}
        {detail.compliance.certifications.filter((c: SupplierCert) => c.status === 'current').length > 0 && (
          <Badge tone="accent">{detail.compliance.certifications.filter((c: SupplierCert) => c.status === 'current').length} active cert{detail.compliance.certifications.filter((c: SupplierCert) => c.status === 'current').length !== 1 ? 's' : ''}</Badge>
        )}
        {detail.trust_page_url && <Badge>Trust page</Badge>}
      </div>

      <div role="tablist" style={{ display: 'flex', gap: 'var(--space-1)', borderBottom: '1px solid var(--border)' }}>
        {TABS.map((t) => (
          <button key={t.value} role="tab" aria-selected={activeTab === t.value} onClick={() => setActiveTab(t.value)}
            style={{
              padding: 'var(--space-2) var(--space-3)', border: 'none',
              borderBottom: activeTab === t.value ? '2px solid var(--accent)' : '2px solid transparent',
              background: 'transparent', color: activeTab === t.value ? 'var(--text)' : 'var(--text-muted)',
              fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-2xs)',
              fontWeight: activeTab === t.value ? 600 : 400, cursor: 'pointer',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'profile' && <ProfileTab detail={detail} slug={slug} onUpdated={() => q.refetch()} />}
      {activeTab === 'certifications' && <CertificationsTab detail={detail} slug={slug} onUpdated={() => q.refetch()} />}
      {activeTab === 'data' && <DataHandlingTab detail={detail} slug={slug} onUpdated={() => q.refetch()} />}
      {activeTab === 'risk' && <RiskTab detail={detail} slug={slug} onUpdated={() => q.refetch()} />}
      {activeTab === 'usedby' && <UsedByTab slug={slug} entryId={detail.portfolio_entry_id} />}
    </div>
  );
}

function ProfileTab({ detail, slug, onUpdated }: { detail: SupplierComplianceDetailRow; slug: string; onUpdated: () => void }) {
  const [trustUrl, setTrustUrl] = useState(detail.trust_page_url ?? '');
  const [statusUrl, setStatusUrl] = useState(detail.status_page_url ?? '');
  const [contact, setContact] = useState(detail.incident_contact ?? '');
  const [dpa, setDpa] = useState(detail.dpa_url ?? '');
  const [renewal, setRenewal] = useState(detail.contract_renewal_at ?? '');
  const [notes, setNotes] = useState(detail.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = useCallback(async () => {
    setBusy(true); setSaved(false);
    try {
      await updateSupplierProfile(slug, detail.portfolio_entry_id, {
        trust_page_url: trustUrl || undefined,
        status_page_url: statusUrl || undefined,
        incident_contact: contact || undefined,
        dpa_url: dpa || undefined,
        contract_renewal_at: renewal || undefined,
        notes: notes || undefined,
      });
      setSaved(true); onUpdated();
    } finally { setBusy(false); }
  }, [slug, detail.portfolio_entry_id, trustUrl, statusUrl, contact, dpa, renewal, notes, onUpdated]);

  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: 600 }}>
      <Field id="sp-trust" label="Trust / security page URL">
        <div>
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <input id="sp-trust" value={trustUrl} onChange={(e) => setTrustUrl(e.target.value)} placeholder="https://trust.example.com"
              style={{ flex: 1, padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-input, var(--radius-card))', background: 'var(--surface)', color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }} />
            {detail.trust_page_url ? (
              <Button variant="ghost" compact disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await checkSupplierTrustPage(slug, detail.portfolio_entry_id);
                    onUpdated();
                  } finally { setBusy(false); }
                }}>
                Check now
              </Button>
            ) : null}
          </div>
          {detail.trust_page_status ? (
            <div style={{ marginTop: 'var(--space-1)', fontSize: 'var(--font-size-3xs)', color: detail.trust_page_status === 'live' ? 'var(--success, var(--accent))' : 'var(--warning)' }}>
              Status: {detail.trust_page_status}
              {detail.trust_page_last_checked_at ? ` · checked ${detail.trust_page_last_checked_at.slice(0, 10)}` : ''}
            </div>
          ) : null}
        </div>
      </Field>
      <Field id="sp-status" label="Status page URL">
        <input id="sp-status" value={statusUrl} onChange={(e) => setStatusUrl(e.target.value)} placeholder="https://status.example.com"
          style={{ width: '100%', padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-input, var(--radius-card))', background: 'var(--surface)', color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }} />
      </Field>
      <Field id="sp-contact" label="Security / incident contact">
        <input id="sp-contact" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="security@example.com"
          style={{ width: '100%', padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-input, var(--radius-card))', background: 'var(--surface)', color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }} />
      </Field>
      <Field id="sp-dpa" label="DPA / data processing agreement URL">
        <input id="sp-dpa" value={dpa} onChange={(e) => setDpa(e.target.value)} placeholder="https://example.com/dpa"
          style={{ width: '100%', padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-input, var(--radius-card))', background: 'var(--surface)', color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }} />
      </Field>
      <Field id="sp-renewal" label="Contract renewal date">
        <input id="sp-renewal" type="date" value={renewal} onChange={(e) => setRenewal(e.target.value)}
          style={{ padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-input, var(--radius-card))', background: 'var(--surface)', color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }} />
      </Field>
      <Field id="sp-notes" label="Notes">
        <textarea id="sp-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes about this supplier"
          style={{ width: '100%', padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-input, var(--radius-card))', background: 'var(--surface)', color: 'var(--text)', fontSize: 'var(--font-size-2xs)', resize: 'vertical' }} />
      </Field>
      {saved ? <Alert variant="success">Profile saved.</Alert> : null}
      <div><Button variant="primary" compact disabled={busy} onClick={handleSave}>{busy ? 'Saving...' : 'Save profile'}</Button></div>

      {/* Disclosure view */}
      <DisclosureView slug={slug} entryId={detail.portfolio_entry_id} />

      {/* Disclosure request */}
      <DisclosureRequestSection slug={slug} entryId={detail.portfolio_entry_id} supplierName={detail.supplier_display_name} />
    </div>
  );
}

function DisclosureView({ entryId }: { slug?: string; entryId: string }) {
  const q = useWorkspaceQuery(
    (s) => queryKeys.supplierDisclosure(s, entryId),
    (s) => getSupplierDisclosure(s, entryId),
  );

  if (q.loading) return <Spinner />;
  if (!q.data?.disclosure) return null;

  const d = q.data.disclosure;
  let content: Record<string, unknown> | null = null;
  try { content = JSON.parse(d.content_json); } catch { /* empty */ }

  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 'var(--space-3)', marginTop: 'var(--space-2)' }}>
      <div style={{ fontSize: 'var(--font-size-2xs)', fontWeight: 600, color: 'var(--text)', marginBottom: 'var(--space-2)' }}>
        AI Disclosure (via gacpro network)
      </div>
      <Card>
        <div style={{ padding: 'var(--space-3) var(--space-4)', display: 'grid', gap: 'var(--space-2)', fontSize: 'var(--font-size-2xs)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <Badge tone="accent">Disclosed</Badge>
            <Badge>{d.source ?? 'respond'}</Badge>
            <span style={{ color: 'var(--text-dim)' }}>Version {d.version_id.slice(0, 12)}</span>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <span style={{ color: 'var(--text-dim)', minWidth: 100 }}>Content hash</span>
            <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text)', wordBreak: 'break-all', fontSize: 'var(--font-size-3xs)' }}>{d.content_hash}</span>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <span style={{ color: 'var(--text-dim)', minWidth: 100 }}>Received</span>
            <span style={{ color: 'var(--text)' }}>{d.created_at?.slice(0, 10) ?? '—'}</span>
          </div>
          {content ? (
            <details style={{ marginTop: 'var(--space-1)' }}>
              <summary style={{ cursor: 'pointer', color: 'var(--accent)', fontSize: 'var(--font-size-3xs)' }}>
                View disclosure content
              </summary>
              <pre style={{
                marginTop: 'var(--space-2)', padding: 'var(--space-2)',
                background: 'var(--surface-elevated, var(--surface))',
                border: '1px solid var(--border)', borderRadius: 'var(--radius-card)',
                fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)',
                overflow: 'auto', maxHeight: 300, whiteSpace: 'pre-wrap',
              }}>
                {JSON.stringify(content, null, 2)}
              </pre>
            </details>
          ) : null}
        </div>
      </Card>
    </div>
  );
}

function DisclosureRequestSection({ slug, entryId, supplierName }: { slug: string; entryId: string; supplierName: string }) {
  const [open, setOpen] = useState(false);
  const [domain, setDomain] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const handleSend = useCallback(async () => {
    if (!domain.trim() || !email.trim()) return;
    setBusy(true); setResult(null);
    try {
      await sendDisclosureRequest(slug, {
        domain: domain.trim(),
        contact_email: email.trim(),
        portfolio_entry_id: entryId,
      });
      setResult(`Disclosure request sent to ${email.trim()}`);
      setOpen(false);
    } catch (err) {
      setResult(`Error: ${err instanceof Error ? err.message : String(err)}`);
    } finally { setBusy(false); }
  }, [slug, entryId, domain, email]);

  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 'var(--space-3)', marginTop: 'var(--space-2)' }}>
      <div style={{ fontSize: 'var(--font-size-2xs)', fontWeight: 600, color: 'var(--text)', marginBottom: 'var(--space-2)' }}>
        gacpro network disclosure
      </div>
      {result ? <Alert variant={result.startsWith('Error') ? 'error' : 'success'}>{result}</Alert> : null}
      {!open ? (
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          <span style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text-dim)' }}>
            Request a structured AI disclosure from {supplierName} through the gacpro network.
          </span>
          <Button variant="secondary" compact onClick={() => setOpen(true)}>Request disclosure</Button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-2)', maxWidth: 400 }}>
          <Field id="dr-domain" label="Supplier domain">
            <input id="dr-domain" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="neon.tech"
              style={{ width: '100%', padding: 'var(--space-1) var(--space-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-input, var(--radius-card))', background: 'var(--surface)', color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }} />
          </Field>
          <Field id="dr-email" label="Contact email">
            <input id="dr-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="security@neon.tech"
              style={{ width: '100%', padding: 'var(--space-1) var(--space-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-input, var(--radius-card))', background: 'var(--surface)', color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }} />
          </Field>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button variant="primary" compact disabled={busy || !domain.trim() || !email.trim()} onClick={handleSend}>
              {busy ? 'Sending...' : 'Send request'}
            </Button>
            <Button variant="ghost" compact disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function CertificationsTab({ detail, slug, onUpdated }: { detail: SupplierComplianceDetailRow; slug: string; onUpdated: () => void }) {
  const [certs, setCerts] = useState<SupplierCert[]>(detail.compliance.certifications);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const addCert = useCallback((framework: string) => {
    if (certs.some((c) => c.framework === framework)) return;
    setCerts((prev) => [...prev, { framework, status: 'current', valid_through: null, report_url: null, notes: null }]);
  }, [certs]);

  const updateCert = useCallback((idx: number, updates: Partial<SupplierCert>) => {
    setCerts((prev) => prev.map((c, i) => i === idx ? { ...c, ...updates } : c));
  }, []);

  const removeCert = useCallback((idx: number) => {
    setCerts((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleSave = useCallback(async () => {
    setBusy(true); setSaved(false);
    try {
      await updateSupplierCompliance(slug, detail.portfolio_entry_id, {
        certifications: certs,
        data_handling: detail.compliance.data_handling,
      });
      setSaved(true); onUpdated();
    } finally { setBusy(false); }
  }, [slug, detail, certs, onUpdated]);

  const unaddedFrameworks = CERT_FRAMEWORKS.filter((f) => !certs.some((c) => c.framework === f.key));

  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      {certs.length === 0 ? (
        <div style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-2xs)' }}>No certifications recorded.</div>
      ) : (
        certs.map((cert, idx) => {
          const fw = CERT_FRAMEWORKS.find((f) => f.key === cert.framework);
          const token = CERT_STATUS_TOKENS[cert.status] ?? CERT_STATUS_TOKENS.pending;
          const isExpired = cert.valid_through && new Date(cert.valid_through) < new Date();
          return (
            <Card key={cert.framework}>
              <div style={{ padding: 'var(--space-3) var(--space-4)', display: 'grid', gap: 'var(--space-2)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }}>{fw?.label ?? cert.framework}</span>
                    <span style={{ padding: '1px 6px', borderRadius: 'var(--radius-chip)', border: `1px solid ${isExpired ? 'var(--critical, var(--warning))' : token.color}`, color: isExpired ? 'var(--critical, var(--warning))' : token.color, fontSize: 'var(--font-size-3xs)' }}>
                      {isExpired ? 'Expired' : token.label}
                    </span>
                  </div>
                  <button onClick={() => removeCert(idx)} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 'var(--font-size-3xs)' }}>Remove</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
                  <Field id={`cert-status-${idx}`} label="Status">
                    <Select id={`cert-status-${idx}`} value={cert.status} onChange={(e) => updateCert(idx, { status: e.target.value as any })}>
                      <option value="current">Current</option>
                      <option value="expired">Expired</option>
                      <option value="pending">Pending</option>
                      <option value="not_applicable">N/A</option>
                    </Select>
                  </Field>
                  <Field id={`cert-valid-${idx}`} label="Valid through">
                    <input id={`cert-valid-${idx}`} type="date" value={cert.valid_through ?? ''} onChange={(e) => updateCert(idx, { valid_through: e.target.value || null })}
                      style={{ padding: 'var(--space-1) var(--space-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-input, var(--radius-card))', background: 'var(--surface)', color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }} />
                  </Field>
                </div>
                <Field id={`cert-url-${idx}`} label="Report / certificate URL">
                  <input id={`cert-url-${idx}`} value={cert.report_url ?? ''} onChange={(e) => updateCert(idx, { report_url: e.target.value || null })} placeholder="https://..."
                    style={{ width: '100%', padding: 'var(--space-1) var(--space-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-input, var(--radius-card))', background: 'var(--surface)', color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }} />
                </Field>
                <Field id={`cert-notes-${idx}`} label="Notes">
                  <input id={`cert-notes-${idx}`} value={cert.notes ?? ''} onChange={(e) => updateCert(idx, { notes: e.target.value || null })} placeholder="e.g. Covers Serverless Postgres only"
                    style={{ width: '100%', padding: 'var(--space-1) var(--space-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-input, var(--radius-card))', background: 'var(--surface)', color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }} />
                </Field>
              </div>
            </Card>
          );
        })
      )}

      {unaddedFrameworks.length > 0 ? (
        <div style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
          {unaddedFrameworks.map((f) => (
            <button key={f.key} onClick={() => addCert(f.key)}
              style={{ padding: '4px 10px', borderRadius: 'var(--radius-chip)', border: '1px dashed var(--border)', background: 'transparent', color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
              + {f.label}
            </button>
          ))}
        </div>
      ) : null}

      {saved ? <Alert variant="success">Certifications saved.</Alert> : null}
      <div><Button variant="primary" compact disabled={busy} onClick={handleSave}>{busy ? 'Saving...' : 'Save certifications'}</Button></div>
    </div>
  );
}

function DataHandlingTab({ detail, slug, onUpdated }: { detail: SupplierComplianceDetailRow; slug: string; onUpdated: () => void }) {
  const dh = detail.compliance.data_handling;
  const [categories, setCategories] = useState<string[]>(dh.data_categories ?? []);
  const [regions, setRegions] = useState(dh.regions?.join(', ') ?? '');
  const [encRest, setEncRest] = useState(dh.encryption_at_rest ?? false);
  const [encTransit, setEncTransit] = useState(dh.encryption_in_transit ?? false);
  const [retention, setRetention] = useState(dh.retention_policy ?? '');
  const [subProc, setSubProc] = useState(dh.sub_processors ?? false);
  const [subProcUrl, setSubProcUrl] = useState(dh.sub_processor_url ?? '');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const DATA_CATS = ['customer_data', 'usage_metadata', 'pii', 'phi', 'financial', 'proprietary', 'public'];

  const handleSave = useCallback(async () => {
    setBusy(true); setSaved(false);
    try {
      await updateSupplierCompliance(slug, detail.portfolio_entry_id, {
        certifications: detail.compliance.certifications,
        data_handling: {
          data_categories: categories,
          regions: regions.split(',').map((r) => r.trim()).filter(Boolean),
          encryption_at_rest: encRest,
          encryption_in_transit: encTransit,
          retention_policy: retention || null,
          sub_processors: subProc,
          sub_processor_url: subProcUrl || null,
        },
      });
      setSaved(true); onUpdated();
    } finally { setBusy(false); }
  }, [slug, detail, categories, regions, encRest, encTransit, retention, subProc, subProcUrl, onUpdated]);

  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: 600 }}>
      <Field id="dh-cats" label="Data categories processed">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
          {DATA_CATS.map((cat) => (
            <label key={cat} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--font-size-2xs)', color: 'var(--text)', cursor: 'pointer' }}>
              <input type="checkbox" checked={categories.includes(cat)}
                onChange={(e) => setCategories((prev) => e.target.checked ? [...prev, cat] : prev.filter((c) => c !== cat))} />
              {cat.replace(/_/g, ' ')}
            </label>
          ))}
        </div>
      </Field>
      <Field id="dh-regions" label="Data regions (comma-separated)">
        <input id="dh-regions" value={regions} onChange={(e) => setRegions(e.target.value)} placeholder="us-east-1, eu-west-1"
          style={{ width: '100%', padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-input, var(--radius-card))', background: 'var(--surface)', color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }} />
      </Field>
      <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--font-size-2xs)', color: 'var(--text)', cursor: 'pointer' }}>
          <input type="checkbox" checked={encRest} onChange={(e) => setEncRest(e.target.checked)} /> Encryption at rest
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--font-size-2xs)', color: 'var(--text)', cursor: 'pointer' }}>
          <input type="checkbox" checked={encTransit} onChange={(e) => setEncTransit(e.target.checked)} /> Encryption in transit
        </label>
      </div>
      <Field id="dh-retention" label="Retention policy">
        <input id="dh-retention" value={retention} onChange={(e) => setRetention(e.target.value)} placeholder="e.g. Customer-controlled; deleted on project deletion"
          style={{ width: '100%', padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-input, var(--radius-card))', background: 'var(--surface)', color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }} />
      </Field>
      <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'center' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--font-size-2xs)', color: 'var(--text)', cursor: 'pointer' }}>
          <input type="checkbox" checked={subProc} onChange={(e) => setSubProc(e.target.checked)} /> Uses sub-processors
        </label>
        {subProc ? (
          <input value={subProcUrl} onChange={(e) => setSubProcUrl(e.target.value)} placeholder="Sub-processor list URL"
            style={{ flex: 1, padding: 'var(--space-1) var(--space-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-input, var(--radius-card))', background: 'var(--surface)', color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }} />
        ) : null}
      </div>
      {saved ? <Alert variant="success">Data handling saved.</Alert> : null}
      <div><Button variant="primary" compact disabled={busy} onClick={handleSave}>{busy ? 'Saving...' : 'Save data handling'}</Button></div>
    </div>
  );
}

function RiskTab({ detail, slug, onUpdated }: { detail: SupplierComplianceDetailRow; slug: string; onUpdated: () => void }) {
  const [tier, setTier] = useState(detail.risk_tier ?? '');
  const [date, setDate] = useState(detail.assessment_date ?? '');
  const [assessor, setAssessor] = useState(detail.assessor ?? '');
  const [rationale, setRationale] = useState(detail.assessment_rationale ?? '');
  const [nextReview, setNextReview] = useState(detail.next_review_at ?? '');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = useCallback(async () => {
    setBusy(true); setSaved(false);
    try {
      await updateSupplierAssessment(slug, detail.portfolio_entry_id, {
        risk_tier: tier || undefined,
        assessment_date: date || undefined,
        assessor: assessor || undefined,
        assessment_rationale: rationale || undefined,
        next_review_at: nextReview || undefined,
      });
      setSaved(true); onUpdated();
    } finally { setBusy(false); }
  }, [slug, detail.portfolio_entry_id, tier, date, assessor, rationale, nextReview, onUpdated]);

  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: 600 }}>
      <Field id="ra-tier" label="Risk tier">
        <Select id="ra-tier" value={tier} onChange={(e) => setTier(e.target.value)}>
          {RISK_TIERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </Select>
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
        <Field id="ra-date" label="Assessment date">
          <input id="ra-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
            style={{ padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-input, var(--radius-card))', background: 'var(--surface)', color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }} />
        </Field>
        <Field id="ra-next" label="Next review">
          <input id="ra-next" type="date" value={nextReview} onChange={(e) => setNextReview(e.target.value)}
            style={{ padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-input, var(--radius-card))', background: 'var(--surface)', color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }} />
        </Field>
      </div>
      <Field id="ra-assessor" label="Assessed by">
        <input id="ra-assessor" value={assessor} onChange={(e) => setAssessor(e.target.value)} placeholder="Name or team"
          style={{ width: '100%', padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-input, var(--radius-card))', background: 'var(--surface)', color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }} />
      </Field>
      <Field id="ra-rationale" label="Assessment rationale">
        <textarea id="ra-rationale" rows={3} value={rationale} onChange={(e) => setRationale(e.target.value)} placeholder="Key findings and risk factors"
          style={{ width: '100%', padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-input, var(--radius-card))', background: 'var(--surface)', color: 'var(--text)', fontSize: 'var(--font-size-2xs)', resize: 'vertical' }} />
      </Field>
      {saved ? <Alert variant="success">Assessment saved.</Alert> : null}
      <div><Button variant="primary" compact disabled={busy} onClick={handleSave}>{busy ? 'Saving...' : 'Save assessment'}</Button></div>
    </div>
  );
}

function UsedByTab({ entryId }: { slug?: string; entryId: string }) {
  const q = useWorkspaceQuery(
    (s) => queryKeys.supplierDependents(s, entryId),
    (s) => getSupplierDependents(s, entryId),
  );

  if (q.loading) return <Spinner />;
  const deps = q.data?.dependents ?? [];

  if (deps.length === 0) {
    return (
      <EmptyState
        title="Not used by any systems"
        description="When a Building-lane system adds this supplier as a dependency, it will appear here."
      />
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
      {deps.map((d, i) => (
        <div key={i} style={{
          display: 'flex', gap: 'var(--space-3)', alignItems: 'center',
          padding: 'var(--space-2) var(--space-3)',
          border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', background: 'var(--surface)',
          fontSize: 'var(--font-size-2xs)',
        }}>
          <span style={{ fontWeight: 600, color: 'var(--text)', flex: 1 }}>{d.system_name}</span>
          <Badge>{d.lane}</Badge>
          <Badge tone="neutral">{d.dependency_type}</Badge>
        </div>
      ))}
    </div>
  );
}
